// Expense Management (payroll-service, via the gateway). Gateway enforces the
// `expenses` entitlement; the backend enforces canonical expenses:* RBAC.
// Amounts travel as DECIMAL STRINGS end to end — never floats.
import axios from 'axios';
import { attachCookieAuth } from '@/lib/session';

const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const api = axios.create({ baseURL: BASE, timeout: 30000, withCredentials: true });
api.interceptors.request.use(attachCookieAuth);

export type ExpenseStatus =
  | 'DRAFT' | 'SUBMITTED' | 'MANAGER_REVIEW' | 'FINANCE_REVIEW' | 'CHANGES_REQUESTED'
  | 'APPROVED' | 'REJECTED' | 'WITHDRAWN' | 'SCHEDULED' | 'REIMBURSED'
  | 'PAYMENT_FAILED' | 'CANCELLED';

export interface ExpenseReport {
  id: string;
  employeeId: string;
  reportNumber: string;
  title: string;
  status: ExpenseStatus;
  currencyTotals: Record<string, string>;
  baseCurrency: string;
  baseTotal: string;
  submittedAt?: string | null;
  currentStage: string;
  version: number;
  createdAt: string;
}

export interface ExpenseItem {
  id: string;
  reportId: string;
  categoryId: string;
  txnDate: string;
  vendor: string;
  description: string;
  amount: string;
  currency: string;
  fxRate?: string | null;
  baseAmount: string;
  allocation?: Record<string, unknown>;
  logistics?: Record<string, unknown>;
  duplicateCheck?: unknown;
  policyResult?: unknown;
}

export interface ExpenseCategory {
  id: string;
  code: string;
  name: string;
  description: string;
  config: Record<string, unknown>;
  glAccount: string;
  displayOrder: number;
  active: boolean;
}

export interface ExpenseReceipt {
  id: string;
  reportId: string;
  itemId?: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  uploadedAt: string;
}

export interface ApprovalEntry {
  stage: string;
  actorId: string;
  decision: string;
  reason: string;
  prevStatus: string;
  newStatus: string;
  createdAt: string;
}

export interface PolicyFinding {
  rule: string;
  outcome: 'PASS' | 'WARNING' | 'JUSTIFICATION_REQUIRED' | 'BLOCKED';
  message: string;
}

export interface Reimbursement {
  id: string;
  reportId: string;
  method: string;
  amount: string;
  currency: string;
  status: string;
  payoutRef: string;
  scheduledAt: string;
  settledAt?: string | null;
}

/**
 * Who a FINANCE_REVIEW claim is waiting on. The rule (payroll inbox, decision
 * and notification alike): the finance stage is handled by people who hold
 * expenses:finance for the expense's company.
 */
export interface FinanceStage {
  legalEntityId?: string;
  legalEntityName?: string;
  /** The claim has no company — only all-companies Finance can act. */
  companyMissing: boolean;
  /** How many people can act; null = could not be determined. */
  approvers: number | null;
}

export interface ReportDetail {
  report: ExpenseReport;
  items: ExpenseItem[];
  receipts: ExpenseReceipt[];
  history: ApprovalEntry[];
  reimbursement: Reimbursement | null;
  /** Present only while the claim is at the finance stage. */
  financeStage?: FinanceStage | null;
}

const V1 = '/api/v1/expenses';

export const listMyReports = (statuses?: string[]) =>
  api.get<{ reports: ExpenseReport[] | null }>(`${V1}/me/reports`, {
    params: statuses?.length ? { status: statuses.join(',') } : undefined,
  }).then((r) => r.data.reports ?? []);

export const createReport = (title: string, baseCurrency = 'TZS') =>
  api.post<ExpenseReport>(`${V1}/reports`, { title, baseCurrency }).then((r) => r.data);

export const getReport = (id: string) =>
  api.get<ReportDetail>(`${V1}/reports/${id}`).then((r) => ({
    ...r.data,
    items: r.data.items ?? [],
    receipts: r.data.receipts ?? [],
    history: r.data.history ?? [],
  }));

export interface ItemInput {
  categoryId: string;
  txnDate: string; // YYYY-MM-DD
  vendor: string;
  description: string;
  amount: string;
  currency: string;
  fxRate?: string;
  allocation?: Record<string, unknown>;
  logistics?: Record<string, unknown>;
}

export const addItem = (reportId: string, item: ItemInput) =>
  api.post<{ id: string }>(`${V1}/reports/${reportId}/items`, item).then((r) => r.data);

export const updateItem = (reportId: string, itemId: string, item: ItemInput) =>
  api.patch(`${V1}/reports/${reportId}/items/${itemId}`, item).then((r) => r.data);

export const deleteItem = (reportId: string, itemId: string) =>
  api.delete(`${V1}/reports/${reportId}/items/${itemId}`).then((r) => r.data);

export const submitReport = (reportId: string) =>
  api.post<{ report: ExpenseReport; findings: PolicyFinding[] | null }>(`${V1}/reports/${reportId}/submit`)
    .then((r) => ({ report: r.data.report, findings: r.data.findings ?? [] }));

// --- stopping a report -------------------------------------------------------
//
// Three tiers, mirroring the server (internal/domain/expense/cancellation.go).
// The UI must agree with it exactly, because disagreeing means offering a
// button the backend then refuses.

export type ExpenseCancelReason =
  | 'mistake' | 'duplicate' | 'trip_cancelled' | 'covered_elsewhere' | 'not_needed' | 'other';

/** Ordered as the server lists them — 'mistake' is first because it is the
 *  commonest honest reason, and when it is not offered people pick something
 *  inaccurate and every other count becomes untrustworthy. */
export const EXPENSE_CANCEL_REASONS: { code: ExpenseCancelReason; label: string }[] = [
  { code: 'mistake', label: 'Created by mistake' },
  { code: 'duplicate', label: 'Already claimed' },
  { code: 'trip_cancelled', label: 'Trip cancelled' },
  { code: 'covered_elsewhere', label: 'Paid another way' },
  { code: 'not_needed', label: 'No longer needed' },
  { code: 'other', label: 'Other' },
];

/** Cancellable at all: before a reviewer has started. WITHDRAWN is included
 *  because it is back in the owner's hands — without it a withdrawn report could
 *  only ever be resubmitted unchanged. */
export const canCancelReport = (status: ExpenseStatus): boolean =>
  status === 'DRAFT' || status === 'SUBMITTED'
  || status === 'CHANGES_REQUESTED' || status === 'WITHDRAWN';

/** A reason is owed once anyone else has seen it. DRAFT is the only status that
 *  answers no: nothing transitions INTO draft, so a draft has never been
 *  submitted. */
export const cancelReasonRequired = (status: ExpenseStatus): boolean =>
  status === 'SUBMITTED' || status === 'CHANGES_REQUESTED' || status === 'WITHDRAWN';

/** Discardable keys on having been SUBMITTED, not on the status: a report that
 *  was submitted, withdrawn and left as a draft reads as DRAFT here too, but an
 *  approver has seen it and it must not silently vanish. */
export const canDiscardReport = (r: Pick<ExpenseReport, 'status' | 'submittedAt'>): boolean =>
  r.status === 'DRAFT' && !r.submittedAt;

export interface CancelInput { reason?: string; note?: string }

export const withdrawReport = (reportId: string, body: CancelInput = {}) =>
  api.post<ExpenseReport>(`${V1}/reports/${reportId}/withdraw`, body).then((r) => r.data);

export const cancelReport = (reportId: string, body: CancelInput = {}) =>
  api.post<ExpenseReport>(`${V1}/reports/${reportId}/cancel`, body).then((r) => r.data);

export const discardReport = (reportId: string) =>
  api.post<{ discarded: boolean }>(`${V1}/reports/${reportId}/discard`).then((r) => r.data);

export const uploadReceipt = (reportId: string, file: File, itemId?: string) => {
  const form = new FormData();
  form.append('file', file);
  if (itemId) form.append('itemId', itemId);
  return api.post<{ id: string; sha256: string }>(`${V1}/reports/${reportId}/receipts`, form).then((r) => r.data);
};

export const receiptDownload = (receiptId: string) =>
  api.get<{ url: string; fileName: string }>(`${V1}/receipts/${receiptId}/download`).then((r) => r.data);

// --- reviewer / finance ---
export const listApprovals = () =>
  api.get<{ reports: ExpenseReport[] | null }>(`${V1}/approvals`).then((r) => r.data.reports ?? []);

export const approveReport = (id: string, version: number) =>
  api.post<ExpenseReport>(`${V1}/reports/${id}/approve`, { version }).then((r) => r.data);

export const rejectReport = (id: string, reason: string, version: number) =>
  api.post<ExpenseReport>(`${V1}/reports/${id}/reject`, { reason, version }).then((r) => r.data);

export const requestChanges = (id: string, reason: string, version: number) =>
  api.post<ExpenseReport>(`${V1}/reports/${id}/request-changes`, { reason, version }).then((r) => r.data);

export const reimburseReport = (id: string, method: 'manual' | 'payroll', reference: string, version: number) =>
  api.post(`${V1}/reports/${id}/reimburse`, {
    method, reference, version,
    idempotencyKey: `${id}:${method}`, // deterministic — a retry replays, never double-pays
  }).then((r) => r.data);

// --- admin ---
export const listCategories = () =>
  api.get<{ categories: ExpenseCategory[] | null }>(`${V1}/categories`).then((r) => r.data.categories ?? []);

export const upsertCategory = (c: Partial<ExpenseCategory> & { code: string; name: string }) =>
  api.post<{ id: string }>(`${V1}/categories`, c).then((r) => r.data);

export const getPolicy = () =>
  api.get<{ version: number; rules: Record<string, unknown> }>(`${V1}/policies`).then((r) => r.data);

export const savePolicy = (rules: Record<string, unknown>) =>
  api.post<{ version: number }>(`${V1}/policies`, { rules }).then((r) => r.data);

export const publishPolicy = (version: number) =>
  api.post(`${V1}/policies/publish`, { version }).then((r) => r.data);

// --- receipt evidence (receipt-first model) ---
export interface EvidenceUpdate {
  documentType?: string;
  countryCode?: string;
  fiscalDocumentNumber?: string;
  supplierName?: string;
  supplierTaxId?: string;
  supplierVatId?: string;
  verificationCode?: string;
  currency?: string;
  totalAmount?: string;
  taxAmount?: string;
}

export const updateReceiptEvidence = (receiptId: string, upd: EvidenceUpdate) =>
  api.patch<{ complianceStatus: string; packVersion: string; notes: string[] }>(
    `${V1}/receipts/${receiptId}`, upd).then((r) => r.data);

export const reviewReceipt = (receiptId: string, review: 'accepted' | 'returned' | 'rejected', markVerified = false) =>
  api.post(`${V1}/receipts/${receiptId}/review`, { review, markVerified }).then((r) => r.data);

export const declareMissingReceipt = (reportId: string, reason: string, itemId?: string, country = 'TZ') =>
  api.post<{ id: string }>(`${V1}/reports/${reportId}/missing-receipt`, { reason, itemId, country }).then((r) => r.data);

// --- cash advances ---
export interface ExpenseAdvance {
  id: string;
  employeeId: string;
  purpose: string;
  amount: string;
  currency: string;
  status: string;
  amountSpent: string;
  amountReturned: string;
  amountOwed: string;
  version: number;
  createdAt: string;
}

export const listAdvances = () =>
  api.get<{ advances: ExpenseAdvance[] | null }>(`${V1}/advances`).then((r) => r.data.advances ?? []);

export const requestAdvance = (purpose: string, amount: string, currency = 'TZS') =>
  api.post<ExpenseAdvance>(`${V1}/advances`, { purpose, amount, currency }).then((r) => r.data);

export const advanceAction = (id: string, action: 'approve' | 'reject' | 'issue' | 'record-return' | 'reconcile' | 'close',
  body?: { returned?: string; version?: number }) =>
  api.post<ExpenseAdvance>(`${V1}/advances/${id}/${action}`, body ?? {}).then((r) => r.data);

export const linkAdvanceReport = (advanceId: string, reportId: string) =>
  api.post<ExpenseAdvance>(`${V1}/advances/${advanceId}/link-report`, { reportId }).then((r) => r.data);

// --- reimbursement settlement + register export ---
export const settleReimbursement = (reimbursementId: string, outcome: 'paid' | 'failed', reference = '', reason = '') =>
  api.post(`${V1}/reimbursements/${reimbursementId}/settle`, { outcome, reference, reason }).then((r) => r.data);

export const exportRegister = () =>
  api.get(`${V1}/reports/export`, { responseType: 'blob' }).then((r) => {
    const url = URL.createObjectURL(r.data as Blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'expense-register.csv'; a.click();
    URL.revokeObjectURL(url);
  });

// --- OCR assist (ai-agent vision) — suggestions only; user confirms every field ---
export interface ExtractResult {
  extracted: boolean;
  reason?: string;
  fields?: Record<string, string | null>;
  confidence?: Record<string, number>;
  overallConfidence?: number;
}

export const extractReceipt = (file: File): Promise<ExtractResult> => {
  const form = new FormData();
  form.append('file', file);
  return api.post<ExtractResult>('/api/v1/ai/expense/extract-receipt', form, { timeout: 45000 })
    .then((r) => r.data)
    .catch(() => ({ extracted: false, reason: 'extraction_failed' }));
};

// --- Imprest monthly cycle -----------------------------------------------------
// Design: docs/expenses/03-imprest-retirement-and-reimbursement-sla.md.
//
// Note the two amounts on every item. `snapshotOutstanding` is what the employee
// was told on the cutoff date and never changes; `liveOutstanding` is what is
// owed now. They are separate fields because showing only one of them is how a
// notice and a screen come to disagree with nobody able to say which is right.

export interface ImprestCycle {
  id: string;
  cycleKey: string;        // '2026-09'
  cutoffDate: string;
  status: 'DRAFT' | 'IN_REVIEW' | 'PUBLISHED' | 'CLOSED' | 'CANCELLED';
  snapshotAt: string;
  publishedAt?: string | null;
  itemCount: number;
  employeeCount: number;
}

export interface ImprestCycleItem {
  id: string;
  advanceId: string;
  employeeId: string;
  currency: string;
  purpose: string;
  reconcileDue?: string | null;
  notYetDue: boolean;          // shown separately, never escalated
  daysOverdue: number;
  snapshotOutstanding: string; // frozen on the cutoff date
  liveOutstanding: string;     // what is owed NOW
  liveStatus: string;
  state: 'OUTSTANDING' | 'CLEARED' | 'EXCLUDED' | 'DISPUTED' | 'WRITTEN_OFF';
  excludedReason?: string;
  extendedDue?: string | null;  // set only when a late publish granted an exception
  exceptionReason?: string;
}

export interface ReimbursementSLAClaim {
  reportId: string;
  reportNumber: string;
  currency: string;
  amount: string;
  approvedAt: string;
  dueAt: string;           // fixed at final approval — never moves
  paidAt?: string | null;
  status: string;
  slaMet: boolean;
  breached: boolean;
  recoveryDays?: number | null;  // only when a payment failed and was retried
}

const IMP = `${V1}/imprest`;

/** The employee's own imprests. The server parameterises by the caller's id. */
export const myImprests = () =>
  api.get<{ cycles: { cycle: ImprestCycle; items: ImprestCycleItem[] }[] }>(`${IMP}/me`)
    .then((r) => r.data.cycles ?? []);

export const listImprestCycles = () =>
  api.get<{ cycles: ImprestCycle[] }>(`${IMP}/cycles`).then((r) => r.data.cycles ?? []);

export const getImprestCycle = (id: string) =>
  api.get<{ cycle: ImprestCycle; items: ImprestCycleItem[] }>(`${IMP}/cycles/${id}`).then((r) => r.data);

/** Freeze the current period. Idempotent; creates no notices and sends nothing. */
export const snapshotImprestCycle = (cutoff?: string) =>
  api.post<{ cycle: ImprestCycle; replayed: boolean }>(`${IMP}/cycles/snapshot`, cutoff ? { cutoff } : {})
    .then((r) => r.data);

export const excludeImprestItem = (cycleId: string, itemId: string, reason: string) =>
  api.post(`${IMP}/cycles/${cycleId}/items/${itemId}/exclude`, { reason }).then((r) => r.data);

/** Records the notices and emits the event. Delivery is the notification
 *  service's decision; the response reports whether sending is even enabled. */
export const publishImprestCycle = (cycleId: string) =>
  api.post<{
    cycle: ImprestCycle; dueBy: string; deadlineBasis: string;
    deadlineNeedsReview: boolean; deadlineNote: string;
    deadlineExceptions: number; sendingEnabled: boolean;
  }>(`${IMP}/cycles/${cycleId}/publish`, {}).then((r) => r.data);

export const reimbursementSLA = () =>
  api.get<{ slaDays: number; basis: string; claims: ReimbursementSLAClaim[] }>(`${V1}/reimbursements/sla`)
    .then((r) => r.data);

// --- imprest configuration -----------------------------------------------------
//
// A NARROW surface, deliberately separate from the expense policy endpoints. The
// backend merges this block into the tenant's rules document, so a caller who
// holds only expenses:imprest:configure (HR) cannot reach the spend limits and
// receipt thresholds that live alongside it.

export interface ImprestReminderConfig {
  enabled: boolean;
  channels: string[]; // 'email' | 'in_app'
}

/** How the tenant's imprest email presents itself. A NAME and a reply-to only —
 *  never a sending address. The mail is genuinely sent from the platform domain
 *  and must keep being, or it fails the tenant's DMARC and lands in spam. */
export interface ImprestSenderIdentity {
  display_name: string;
  reply_to: string;
}

export interface ImprestPolicy {
  cutoff_day: number;
  reminder_time: string;
  timezone: string;
  policy_reminder: ImprestReminderConfig;
  private_notices: ImprestReminderConfig;
  eligible_advances: { statuses: string[]; include_not_yet_due: boolean };
  grace: { value: number; basis: string; clock_starts: string; late_publish_floor_days: number };
  reimbursement_sla: { value: number; basis: string };
  recipients: { hr_report_named: boolean; escalation_owner: string };
  review_sla_hours: number;
  sender: ImprestSenderIdentity;
  /** The language the notice and reminder are WRITTEN in. A tenant setting, not
   *  a per-person one: the platform stores no language against a user (the web
   *  switcher is browser-local and never persisted), so there is nothing
   *  per-recipient to honour. '' or an unknown code means English. */
  message_language: string;
}

/** Whether each sender would actually put mail in an inbox. Computed by the
 *  BACKEND from the same rule it applies when sending — the screen must never
 *  recompute "enabled AND channels includes email", because two implementations
 *  of one rule is how a page reassures someone that sending is off while it is
 *  on. */
export interface ImprestPolicyEffective {
  policyReminderEmails: boolean;
  privateNoticeEmails: boolean;
  /** The From display name a recipient actually sees, rendered by the SAME code
   *  that puts it on the envelope. The screen must never assemble this itself —
   *  a preview that disagrees with the real header is worse than none. */
  fromName: string;
  replyTo: string;
}

export interface ImprestPolicyResponse {
  policy: ImprestPolicy;
  version: number;
  effective: ImprestPolicyEffective;
}

export const getImprestPolicy = () =>
  api.get<ImprestPolicyResponse>(`${IMP}/policy`).then((r) => r.data);

export const saveImprestPolicy = (policy: ImprestPolicy) =>
  api.put<ImprestPolicyResponse>(`${IMP}/policy`, policy).then((r) => r.data);
